// State machine for the Live In Club page composition. Models the
// two UI-controlled inputs the page owns: the selected filter tab
// (All / In Club / Not In Club) and the current search input value.
// Tanstack Query owns its own loading / error / success state via
// `useQuery`'s return; this machine deliberately does NOT mirror that
// — query state is consumed directly from the hook so we don't keep
// two sources of truth in sync.
//
// First xstate use in the codebase (xstate 5.31.1 installed in PR 3
// via #57). The page-level shape is the right place for a state
// machine because it coordinates multiple inputs (tab + search) that
// together drive list filtering; design-system primitives stay
// stateless and controlled.

import { assign, type StateValueFrom, setup } from 'xstate';

import type { StatusValue } from '../../domain/status';

export type FilterTab = 'all' | StatusValue;

export interface LiveInClubPageContext {
	selected_tab: FilterTab;
	search_value: string;
}

export type LiveInClubPageEvent =
	| { type: 'SELECT_TAB'; value: FilterTab }
	| { type: 'UPDATE_SEARCH'; value: string }
	| { type: 'CLEAR_SEARCH' };

export const liveInClubPageMachine = setup({
	types: {
		context: {} as LiveInClubPageContext,
		events: {} as LiveInClubPageEvent,
	},
}).createMachine({
	id: 'liveInClubPage',
	initial: 'idle',
	context: {
		selected_tab: 'all',
		search_value: '',
	},
	states: {
		idle: {
			on: {
				// Inline `assign` here so xstate narrows `event` to the
				// specific event type for each on-handler — avoids the
				// defensive fallback branches a named-actions setup would
				// require.
				SELECT_TAB: {
					actions: assign({ selected_tab: ({ event }) => event.value }),
				},
				UPDATE_SEARCH: {
					actions: assign({ search_value: ({ event }) => event.value }),
				},
				CLEAR_SEARCH: {
					actions: assign({ search_value: '' }),
				},
			},
		},
	},
});

export type LiveInClubPageState = StateValueFrom<typeof liveInClubPageMachine>;
